import { Action, ACTION_VARIANTS, ICON_NAMES } from '@reverse-hr/design-system';
import { useStoreActions, useStoreState } from 'easy-peasy';
import { useTranslation } from 'react-i18next';
import {
  jobStoreSelector,
  selectorGetJobProfilingMeetingDocument,
  selectorJobHasProfilingMeetingDocument,
  selectorJobProfilingBoardUrl,
} from '@model/Job/Job.selectors';
import { FC, useCallback } from 'react';
import { selectorUser } from '@model/User';
import { USER_TYPES_MAP } from '@app/constants/session';
import { GTM } from '@app/gtm';
import { DownloadFileButton } from '@components/DownloadFileButton';
import type { TDownloadResponseData } from '@app/_types/downloads.types';

export const ProfilingPageIntroDownload: FC = () => {
  const { t } = useTranslation('', {
    keyPrefix: 'profiling.intro',
  });

  const hasProfilingMeetingDocument = useStoreState(
    selectorJobHasProfilingMeetingDocument,
  );

  const job = useStoreState(jobStoreSelector);
  const { type: userType } = useStoreState(selectorUser);
  const profilingBoardUrl = useStoreState(selectorJobProfilingBoardUrl);

  const getJobProfilingMeetingDocument = useStoreActions(
    selectorGetJobProfilingMeetingDocument,
  );

  const isActionLink = !hasProfilingMeetingDocument && profilingBoardUrl;

  const profilingDocumentFileName = job
    ? `${job.title} - ${job.companyName} - Company Assessment & Candidate Design.pdf`
    : null;

  const trackOpenDocumentClick = useCallback(() => {
    const documentType = isActionLink ? 'Miro' : 'Profiling Meeting Document';

    const eventAction = `${documentType} ${
      userType === USER_TYPES_MAP.MANAGER ? 'prospect' : 'cliente'
    }`;

    GTM.trackGTMEvent({
      action: eventAction,
      label: `${userType} - ${job?.companyName} - ${job?.id} - ${job?.title}`,
    });

    GTM.trackGA4Event(
      eventAction,
      job?.companyName,
      `${userType} - ${job?.id} - ${job?.title}`,
    );
  }, [userType, job?.companyName, job?.id, job?.title]);

  const onDownloadDocumentClick =
    async (): Promise<TDownloadResponseData | null> => {
      if (!job?.id) {
        return null;
      }

      try {
        return await getJobProfilingMeetingDocument({ jobId: job?.id });
      } finally {
        trackOpenDocumentClick();
      }
    };

  if (hasProfilingMeetingDocument) {
    return (
      <DownloadFileButton
        className="c-profiling-page-intro__button"
        fileName={profilingDocumentFileName ?? ''}
        label={t('profilingDocumentCta')}
        onClick={onDownloadDocumentClick}
        variant={ACTION_VARIANTS.OUTLINED}
      />
    );
  }

  if (isActionLink) {
    return (
      <a
        href={profilingBoardUrl}
        target="_blank"
        rel="noopener noreferrer"
        onClick={trackOpenDocumentClick}
      >
        <Action
          className="c-profiling-page-intro__button"
          rightIcon={ICON_NAMES.CHEVRON_BIG_RIGHT}
          variant={ACTION_VARIANTS.OUTLINED}
        >
          {t('profilingDocumentCta')}
        </Action>
      </a>
    );
  }

  return null;
};
